import { useEffect } from "react";
import { useFeatureFlag } from "@apac/feature-flags";
import {
  IconNameEnum,
  useModuleBEM,
  useTenantIcons,
} from "@b2bportal/core-themes";
import { trackEvent } from "@hopper-b2b/api";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { ExperimentVariant, PriceDropEvents } from "@hopper-b2b/types";
import { PriceForecast } from "@hopper-b2b/ui";
import {
  amountToFiatPrice,
  FeatureFlagNames,
  noFractionOptions,
} from "@hsbc/utilities";
import {
  PriceDropDialog,
  type PriceDropDialogProps,
} from "../PriceDropDialog/PriceDropDialog";
import { PricePredictionDialog } from "../PricePredictionDialog/PricePredictionDialog";
import { WatchTripDialog } from "../WatchTripDialog/WatchTripDialog";
import { usePricePrediction } from "./hooks/usePricePrediction";
import styles from "./styles.module.scss";

export const WrappedPriceDropDialog = ({
  children,
  ...props
}: PriceDropDialogProps) => {
  const [_, cn] = useModuleBEM(styles, "PricePredictionCard");
  useEffect(() => {
    trackEvent({
      eventName: PriceDropEvents.viewed_price_drop,
      properties: {},
    });
  }, []);
  return (
    <>
      <span className={cn("price-drop-button")} onClick={props.onOpen}>
        {children}
      </span>
      <PriceDropDialog {...props} />
    </>
  );
};

export const PricePredictionCard = () => {
  const { t, formatFiatCurrency } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "PricePredictionCard");

  const pricePredictionReturn = usePricePrediction();
  const enablePriceWatch =
    useFeatureFlag(
      FeatureFlagNames.ENABLE_PRICE_WATCH,
      ExperimentVariant.Available,
      true
    ) ?? true;

  const icons = useTenantIcons();
  const PricePredictionIcon = icons[IconNameEnum.pricePrediction];

  const {
    context: {
      title,
      subtitle,
      prediction,
      items,
      isWatching,
      isPDPEligible,
      priceDropAmount,
    },
    watchTripDialogProps,
    priceDropDialogProps,
    learnMoreDialogProps,
  } = pricePredictionReturn;

  if (!prediction?.dealness) {
    return null;
  }

  return (
    <div className={block}>
      <div className={cn("icon-container")}>
        <PricePredictionIcon />
      </div>

      <div className={cn("prediction-container")}>
        <span className={cn("prediction-text")}>{title}</span>

        <div className={cn("subtitle-container")}>
          <Trans
            i18nKey={`flightShopPrediction.subtitle${
              isPDPEligible ? "WithPriceDrop" : "WithoutPriceDrop"
            }`}
            components={[
              <span className={cn("price")} />,
              <WrappedPriceDropDialog
                {...priceDropDialogProps}
                priceDropAmount={formatFiatCurrency(
                  amountToFiatPrice(priceDropAmount?.amount),
                  noFractionOptions
                )}
              />,
            ]}
            values={{
              price: formatFiatCurrency(prediction?.lowestPrice?.fiat, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }}
          />
        </div>

        <span className={cn("recommendation-text")}>{subtitle}</span>
      </div>

      <PriceForecast
        className={cn("price-forecast")}
        items={items}
        dealness={prediction?.dealness}
      />

      <div className={cn("action-line")}>
        <div className={cn("learn-more")}>
          <PricePredictionDialog
            className={cn("learn-more-button")}
            {...learnMoreDialogProps}
            watchTripButton={
              enablePriceWatch ? (
                <button
                  className={cn("cta-button")}
                  onClick={() => {
                    watchTripDialogProps.onOpen();
                  }}
                >
                  {isWatching
                    ? t("priceWatch.unwatchCta")
                    : t("priceWatch.watchCta")}
                </button>
              ) : null
            }
            {...pricePredictionReturn}
          >
            {t("learnMore")}
          </PricePredictionDialog>
        </div>

        {enablePriceWatch && (
          <div className={cn("watch-container")}>
            <WatchTripDialog
              className={cn("watch-trip-button")}
              {...watchTripDialogProps}
            >
              {isWatching
                ? t("priceWatch.unwatchCta")
                : t("priceWatch.watchCta")}
            </WatchTripDialog>
          </div>
        )}
      </div>
    </div>
  );
};
